import { mediaQueries, palette, Select } from '@centivo/react-components'
import styled from 'styled-components'

import { SearchBar as DirectorySearchBar } from '@centivo/provider-directory-components'

import { ContentSection } from 'src/refactor/structure'
import { MEMBER_PORTAL_SIGN_IN_URL } from 'src/refactor/helpers/constants'
import { useGlobalState } from 'src/context/global'
import { useDirectorySearchBar } from 'src/refactor/pages/results/functions/useDirectorySearchBar'

const SearchBarHeading = styled.h2`
  color: ${palette.brand.darkGreen};
  font-size: 1.875rem;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 1.5rem;
  text-align: center;
`

const NetworkSelectorSlot = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  .network-selector {
    max-width: 430px;
    width: 100%;

    @media ${mediaQueries.desktop} {
      width: 440px;
      max-width: unset;
    }
  }
`

const SearchBarSlot = styled.div`
  width: 100%;
  max-width: 46rem;
  margin: 0 auto;

  @media ${mediaQueries.mobile} {
    max-width: unset;
  }
`

const DisclaimerList = styled.ul`
  list-style-type: disc;
  margin: 2.5rem auto 0;
  padding-left: 1.25rem;
  max-width: 46rem;

  @media ${mediaQueries.desktop} {
    max-width: 670px;
  }
`

const BulletPoint = styled.li`
  color: #212121;
  font-family: AnekGujarati;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.28px;
  margin-bottom: 10px;
  line-height: 140%;
`

const BulletPointBold = styled(BulletPoint)`
  font-weight: 600;
`

const MemberPortalLink = styled.a`
  color: #5e58f6;
  font-family: AnekGujarati;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.28px;
  text-decoration: underline;
`

const ProviderRelationsLink = styled.a`
  color: #212121;
  font-family: AnekGujarati;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.28px;
  line-height: 140%;
`

const SearchDisclaimer = () => (
  <DisclaimerList>
    <BulletPointBold>
      Before scheduling an appointment, please check the Centivo app or{' '}
      <MemberPortalLink
        href={MEMBER_PORTAL_SIGN_IN_URL}
        target="_blank"
        rel="noreferrer"
      >
        my.centivo.com
      </MemberPortalLink>{' '}
      to make sure the provider is in-network for your plan.
    </BulletPointBold>
    <BulletPoint>
      We recommend calling a provider’s office directly to verify that the
      information is still current. Appointment availability may vary.
    </BulletPoint>
    <BulletPoint>
      If something looks incorrect in the directory, please email us at{' '}
      <ProviderRelationsLink href="mailto:ProviderRelations@centivo.com">
        ProviderRelations@centivo.com
      </ProviderRelationsLink>
      .
    </BulletPoint>
  </DisclaimerList>
)

export const SearchBar = () => {
  const [state, actions] = useGlobalState()
  const { networkSelectionEnabled } = state.appConfig

  const { searchBar, handleSubmit } = useDirectorySearchBar()

  const planNetworkOptions = state.planNetworkMaps.map(
    (plan: { planName: string }) => ({
      value: plan.planName,
      label: plan.planName,
    })
  )

  // When the site requires a network selection, the chosen plan determines the
  // networks sent to the provider-api. Hold the search back until a plan is
  // picked so the request is scoped to the right networks.
  const requiresPlanSelection =
    networkSelectionEnabled && state.selectedPlan === null

  return (
    <ContentSection id="find-a-doctor">
      <SearchBarHeading>Find a doctor or facility</SearchBarHeading>
      {networkSelectionEnabled && (
        <NetworkSelectorSlot>
          <Select
            className="network-selector"
            placeholder="Select your network"
            options={planNetworkOptions}
            onSelect={(value: string) => actions.onSelectPlan(value)}
            isSearchable={false}
            value={state.selectedPlan}
            isClearable
            float
          />
        </NetworkSelectorSlot>
      )}
      {!requiresPlanSelection && (
        <>
          <SearchBarSlot>
            <DirectorySearchBar {...searchBar} onSubmit={handleSubmit} />
          </SearchBarSlot>
          <SearchDisclaimer />
        </>
      )}
    </ContentSection>
  )
}
